import { useEffect, useRef } from 'react';
import { getGiosgRooms, removeAllGiosgVisitors, submitGiosgVisitor } from '../../../common/utils/giosgUtils.js';
import { deepEqual } from '../../../common/utils/objectUtils.js';
import { useAuth } from '../AuthProvider';
import type { GiosgVisitorData } from '../../../common/utils/giosgUtils.js';
import type { AuthenticatedUser } from '../authState';

const POLL_INTERVAL_MS = 2000;

const toGiosgVisitorPayload = (authenticatedUser: AuthenticatedUser): GiosgVisitorData | null => {
  if (!authenticatedUser?.firstName) {
    return null;
  }

  return {
    businessId: authenticatedUser.businessId,
    companyName: authenticatedUser.companyName,
    email: authenticatedUser.email,
    mobile: authenticatedUser.mobile,
    userId: authenticatedUser.mdmId,
    username: `${authenticatedUser.firstName} ${authenticatedUser.lastName ?? ''}`.trim(),
  };
};

export const GiosgVisitorSync = () => {
  const { authenticatedUser } = useAuth();
  const isInitialRun = useRef(true);
  const previousVisitor = useRef<GiosgVisitorData | null>(null);

  useEffect(() => {
    const syncVisitor = () => {
      const rooms = getGiosgRooms();

      const newGiosgVisitorData = authenticatedUser ? toGiosgVisitorPayload(authenticatedUser) : null;

      if (newGiosgVisitorData) {
        if (
          isInitialRun.current ||
          !previousVisitor.current ||
          !deepEqual(previousVisitor.current, newGiosgVisitorData)
        ) {
          const wasSubmitted = submitGiosgVisitor(newGiosgVisitorData, rooms);

          if (wasSubmitted) {
            previousVisitor.current = newGiosgVisitorData;
          }
        }
      } else if (!isInitialRun.current || previousVisitor.current) {
        previousVisitor.current = null;
        removeAllGiosgVisitors(rooms);
      }
      isInitialRun.current = false;
    };

    syncVisitor();
    const intervalId = window.setInterval(syncVisitor, POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [authenticatedUser]);

  return null;
};
